import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  MapPinIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { theme } from '../../styles/theme';
import { formatShareExpiry } from '../../utils/shareExpiryOptions';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.background.primary};
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

const Logo = styled.div`
  display: inline-block;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  padding: 1rem 2rem;
  border-radius: ${theme.borderRadius.xl};
  margin-bottom: 1rem;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3);
`;

const LogoText = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const SubTitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.md};
  margin: 0.5rem 0 0;
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
  margin-bottom: 1rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const VesselTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.h3};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Meta = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  cursor: pointer;

  &:hover {
    background: ${theme.colors.background.tertiary};
  }
`;

const MapWrap = styled.div`
  height: 480px;
  width: 100%;

  .leaflet-container {
    height: 100%;
    width: 100%;
  }
`;

const StatusBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.875rem 1.25rem;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const LoadingState = styled.div`
  text-align: center;
  color: ${theme.colors.text.secondary};
  padding: 4rem 1rem;
`;

const ErrorState = styled.div`
  text-align: center;
  color: ${theme.colors.error.main};
  padding: 4rem 1rem;

  svg {
    width: 3rem;
    height: 3rem;
    margin: 0 auto 1rem;
  }
`;

function RecenterMap({ lat, lon }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lon != null) {
      map.setView([lat, lon], map.getZoom() || 13, { animate: true });
    }
  }, [lat, lon, map]);
  return null;
}

function getApiBaseUrl() {
  const { protocol, hostname, port } = window.location;
  const portSuffix = port && port !== '80' && port !== '443' ? `:${port}` : '';
  return `${protocol}//${hostname}${portSuffix}`;
}

const SharedVesselView = () => {
  const { token } = useParams();
  const [vessel, setVessel] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const loadPosition = useCallback(async () => {
    const response = await fetch(`${getApiBaseUrl()}/api/vessel-tracking-sharing/shared/${token}/position`);
    if (!response.ok) {
      if (response.status === 404) {
        const body = await response.json().catch(() => ({}));
        if (body.error?.includes('expired') || body.error?.includes('not found')) {
          throw new Error(body.error);
        }
        return null;
      }
      throw new Error('Failed to load position');
    }
    const payload = await response.json();
    setPosition(payload.position || null);
    setLastUpdate(new Date());
    return payload.position;
  }, [token]);

  const loadShared = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getApiBaseUrl()}/api/vessel-tracking-sharing/shared/${token}`);
      if (!response.ok) throw new Error('Tracking link not found or expired');
      const payload = await response.json();
      setVessel(payload.vessel);
      setShareInfo(payload.share_info || null);
      setPosition(payload.position || null);
      setLastUpdate(new Date());
    } catch (err) {
      console.error('Error loading shared vessel:', err);
      setError(err.message || 'Failed to load shared tracking');
      toast.error('This tracking link is invalid or has expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadShared();
  }, [loadShared]);

  useEffect(() => {
    if (!token || error) return undefined;
    const interval = setInterval(() => {
      loadPosition().catch((err) => {
        if (err.message?.includes('expired') || err.message?.includes('not found')) {
          setError(err.message);
        }
      });
    }, 10000);
    return () => clearInterval(interval);
  }, [token, error, loadPosition]);

  const handleRefresh = async () => {
    try {
      await loadPosition();
      toast.success('Position refreshed');
    } catch {
      toast.error('Failed to refresh position');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Container>
          <Header>
            <Logo><LogoText>BoatWise</LogoText></Logo>
            <SubTitle>Live Vessel Tracking</SubTitle>
          </Header>
          <LoadingState>Loading vessel position…</LoadingState>
        </Container>
      </PageContainer>
    );
  }

  if (error || !vessel) {
    return (
      <PageContainer>
        <Container>
          <Header>
            <Logo><LogoText>BoatWise</LogoText></Logo>
            <SubTitle>Live Vessel Tracking</SubTitle>
          </Header>
          <ErrorState>
            <ExclamationTriangleIcon />
            <h2>Tracking unavailable</h2>
            <p>{error || 'This link is invalid or has expired.'}</p>
          </ErrorState>
        </Container>
      </PageContainer>
    );
  }

  const ownerLabel = shareInfo?.owner_name ? `Shared by ${shareInfo.owner_name}` : 'Shared live tracking';
  const hasPosition = position?.latitude != null && position?.longitude != null;
  const center = hasPosition
    ? [position.latitude, position.longitude]
    : [43.65, -79.38];

  return (
    <PageContainer>
      <Container>
        <Header>
          <Logo><LogoText>BoatWise</LogoText></Logo>
          <SubTitle>{ownerLabel}</SubTitle>
        </Header>

        <Card>
          <CardHeader>
            <div>
              <VesselTitle>
                <MapPinIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                {vessel.vessel_name}
              </VesselTitle>
              <Meta>
                {vessel.vessel_type && <span style={{ textTransform: 'capitalize' }}>{vessel.vessel_type}</span>}
                {shareInfo?.expires_at !== undefined && (
                  <span> · {formatShareExpiry(shareInfo.expires_at)}</span>
                )}
              </Meta>
            </div>
            <RefreshButton type="button" onClick={handleRefresh}>
              <ArrowPathIcon style={{ width: '1rem', height: '1rem' }} />
              Refresh
            </RefreshButton>
          </CardHeader>

          <MapWrap>
            {hasPosition ? (
              <MapContainer center={center} zoom={13} scrollWheelZoom style={{ height: '100%', width: '100%' }}>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <RecenterMap lat={position.latitude} lon={position.longitude} />
                <Marker position={[position.latitude, position.longitude]}>
                  <Popup>
                    <strong>{vessel.vessel_name}</strong>
                    <br />
                    {position.latitude.toFixed(5)}, {position.longitude.toFixed(5)}
                    <br />
                    {position.isRealtime ? 'Live' : 'Last known'}
                  </Popup>
                </Marker>
              </MapContainer>
            ) : (
              <LoadingState style={{ paddingTop: '8rem' }}>
                No GPS position available yet. Waiting for a fix…
              </LoadingState>
            )}
          </MapWrap>

          <StatusBar>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
              <SignalIcon style={{ width: '1rem', height: '1rem' }} />
              {position?.isRealtime ? 'Live position' : position ? 'Last known position' : 'No fix'}
            </span>
            {hasPosition && (
              <span>
                {position.latitude.toFixed(5)}°, {position.longitude.toFixed(5)}°
              </span>
            )}
            {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
            {position?.timestamp && (
              <span>Fix time {new Date(position.timestamp).toLocaleString()}</span>
            )}
          </StatusBar>
        </Card>
      </Container>
    </PageContainer>
  );
};

export default SharedVesselView;
